// ClientSelectSearch.jsx
import React, {useState, useEffect, useRef} from 'react';
import {Search, X} from 'lucide-react';
import {Link} from 'react-router-dom';
import useApi from "../../../utils/useApi";

function ClientSelectSearch({ clients, currentClient, onSelectClient, includeLink, header = null, placeholder = null }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [filteredClients, setFilteredClients] = useState(clients);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const timeoutRef = useRef(null);
    const { searchClients } = useApi();

    useEffect(() => {
        // When the dropdown is opened, reset the search and focus the input
        if (isDropdownOpen) {
            setSearchTerm('');
            setFilteredClients(clients);
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [isDropdownOpen, clients]);

    useEffect(() => {
        // Close dropdown if clicked outside
        const handleClickOutside = (e) => {
            if (
                isDropdownOpen &&
                containerRef.current &&
                !containerRef.current.contains(e.target)
            ) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    const handleToggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleSearch = async (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        // Debounce
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(async () => {
            if (!clients) return;

            const response = await searchClients({ term: value });

            setFilteredClients(response.clients);
        }, 300);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setFilteredClients(clients);
        inputRef.current?.focus();
    };

    const handleSelect = (client) => {
        onSelectClient(client);
        setIsDropdownOpen(false);
    };

    return (
        <div className="relative w-full" ref={containerRef}>
            {/* Button showing current client. Clicking toggles dropdown */}
            <button
                type="button"
                onClick={handleToggleDropdown}
                className="flex items-center justify-between w-full border border-gray-300 rounded-lg px-4 py-2 bg-white"
            >
        <span className="text-gray-700">
            {placeholder && !currentClient?.client ? (
                placeholder
            ):(
                currentClient?.client || 'Select a client...'
            )}
        </span>
                <Search className="h-5 w-5 text-gray-400 ml-2"/>
            </button>

            {isDropdownOpen && (
                <div className="absolute mt-1 w-full bg-white dark:bg-gray-900 border border-gray-300 rounded-lg shadow-lg z-50">
                    <div className="flex items-center border-b border-gray-200 p-2">
                        <Search className="h-4 w-4 text-gray-400"/>
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder={`${header ? header:'Search for a client...'}`}
                            className="w-full px-2 outline-none"
                        />
                        {searchTerm && (
                            <button
                                onClick={handleClearSearch}
                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                                type="button"
                            >
                                <X className="h-4 w-4 text-gray-400"/>
                            </button>
                        )}
                    </div>

                    <div className="max-h-60 overflow-y-auto">


                        {filteredClients.length > 0 ? (
                            filteredClients.map((client) => (
                                includeLink ? (
                                    <Link
                                        key={client.id}
                                        to={`/client-progress/${client.id}`}
                                        className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                        onClick={() => handleSelect(client)}
                                    >
                                        {client.client}
                                    </Link>
                                ) : (
                                    <span key={client.id}
                                          onClick={() => handleSelect(client)}
                                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                                        {client.client}
                                    </span>
                                )
                            ))
                        ) : (
                             <div className="px-4 py-2 text-gray-400 text-sm">
                                 No matching clients
                             </div>
                         )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClientSelectSearch;
